/**
 * Which of the three Patient Overview tabs a patient opens on.
 *
 * Extracted from `EncountersListPage` so the rules can be tested — they are subtle
 * enough that a regression would be silent, and would show up only as "the wrong tab
 * keeps coming back" in the clinic.
 *
 * Two rules interact:
 *
 *  1. A clinic set to Treatment Record mode has said the Treatment Record IS the
 *     patient workspace, so that is the default tab.
 *  2. `setViewForTab` clears the query string for whichever tab is the DEFAULT, and
 *     writes `?view=` for the others. So the parser must honour an explicit
 *     `?view=encounters`. Without that, a Treatment Record clinic could never stay on
 *     the Patient Timeline: selecting it would clear the param and the default would
 *     pull them straight back.
 */

/** The three tabs of the Patient Overview (PRD.219). */
export type ViewMode = 'encounters' | 'treatment' | 'billing';

const VIEW_MODES: readonly ViewMode[] = ['encounters', 'treatment', 'billing'];

/** The clinic's configured workspace complexity (`patient_workspace_settings`). */
export type WorkspaceMode = 'treatment_record' | 'full_workspace' | (string & {});

/** The tab shown when the URL says nothing. */
export function defaultViewForWorkspaceMode(workspaceMode: WorkspaceMode): ViewMode {
  return workspaceMode === 'treatment_record' ? 'treatment' : 'encounters';
}

/** A `?view=` value, or null when absent or unrecognised. */
export function parseViewParam(rawView: string | null | undefined): ViewMode | null {
  return VIEW_MODES.includes(rawView as ViewMode) ? (rawView as ViewMode) : null;
}

export interface ResolvedView {
  /** The tab to show. */
  viewMode: ViewMode;
  /**
   * True while the answer is still a guess: no explicit `?view=` AND the workspace
   * settings have not loaded, so `workspaceMode` is still the fallback. Rendering a
   * tab body now risks showing one and yanking it away a frame later.
   */
  undecided: boolean;
}

export function resolvePatientOverviewView(input: {
  rawView: string | null | undefined;
  workspaceMode: WorkspaceMode;
  /** Whether `patient_workspace_settings` has actually loaded. */
  workspaceSettingsLoaded: boolean;
}): ResolvedView {
  const explicit = parseViewParam(input.rawView);
  const fallback = defaultViewForWorkspaceMode(input.workspaceMode);
  return {
    viewMode: explicit ?? fallback,
    undecided: !explicit && !input.workspaceSettingsLoaded,
  };
}

/**
 * The next query string for a tab selection: cleared for the default tab, `?view=` for
 * the others. Keeping the default paramless is what makes a plain `/patient/:id/encounters`
 * link follow the clinic's configuration.
 */
export function searchParamsForView(mode: ViewMode, defaultView: ViewMode): Record<string, string> {
  return mode === defaultView ? {} : { view: mode };
}
